
import { computed, defineComponent, reactive, ref, watch } from 'vue';
import { datetimeFormat } from '@/utils';
import { StateChanger } from 'vue-infinite-loading';
import { useRoute, useRouter } from 'vue-router/composables';
import { useStampCard } from '@/compositions/useStampCard';
import SimpleModal from '@/components/molecules/SimpleModal.vue';

const CHANNEL_ID =
  process.env.NODE_ENV === 'local' ? window.CHANNEL_ID || process.env.VUE_APP_CHANNEL_ID : window.CHANNEL_ID;

type State = {
  from: number;
  limit: number;
  stampCards: { [key: string]: Record<string, string> };
  id: string;
};

export default defineComponent({
  name: 'StampCardHistory',

  components: {
    SimpleModal,
  },

  setup(_) {
    const { resolve, getHistory } = useStampCard();
    const router = useRouter();
    const route = useRoute();

    const state = reactive<State>({
      // for pagination
      from: 0,
      limit: 50, // パフォーマンス次第で調整
      stampCards: {},
      id: route.params.id,
    });
    const stampHistories = ref<any[]>([]);
    const currentStampTransfer = ref<any | null>(null);
    const shopDetail = ref<string | null>('');

    const isShopNameModalActive = ref(false);
    const showShopNameDetail = (stampHistory: any) => {
      isShopNameModalActive.value = true;
      shopDetail.value =
        state.stampCards[stampHistory._source.event.resource.key][stampHistory._source.event.origin.key] ??
        'スタンプ定義名なし';
    };

    const validActions = ['stamp', 'goal', 'removeStamp', 'complete', 'expired', 'checkpoint'] as const;
    type ActionType = typeof validActions[number];

    const actionType = (stampHistory: any): ActionType | null => {
      try {
        const action = stampHistory._source.event.action ?? '';
        if (validActions.includes(action)) return action as ActionType;
        return null;
      } catch {
        return null;
      }
    };

    const actionTypeLabel = (stampHistory: any) => {
      const _actionType = actionType(stampHistory);
      switch (_actionType) {
        case 'stamp':
          return 'スタンプ';
        case 'goal':
          return 'ゴール';
        case 'removeStamp':
          return '削除';
        case 'checkpoint':
          return 'チェックポイント';
        case 'complete':
          return 'コンプリート';
        case 'expired':
          return '期限切れ';
        default:
          return '';
      }
    };

    const tagType = (stampHistory: any) => {
      const _actionType = actionType(stampHistory);
      switch (_actionType) {
        case 'stamp':
          return 'is-success';
        case 'goal':
          return 'is-purple';
        case 'removeStamp':
          return 'is-danger';
        case 'checkpoint':
          return 'is-info';
        case 'complete':
          return 'is-primary';
        case 'expired':
          return 'is-warning';
        default:
          return '';
      }
    };

    // スタンプ名取得
    const getStampItemLabel = async (stampHistory: any) => {
      const itemId = stampHistory._source.event?.origin?.key ?? '';
      if (!itemId) return;
      // MEMO: スタンプカードのアイテムは、defaultがあるので取得しない
      if (itemId === 'default') return;

      const resourceType = stampHistory._source.event.resource.type;
      const stampCardId = stampHistory._source.event.resource.key;
      if (resourceType !== 'StampCard') return;
      if (stampCardId == null) return;
      if (state.stampCards[stampCardId]) return;

      try {
        await resolve(stampCardId)
          .then(res => {
            const itemMap: Record<string, string> = {};
            res.result.stampCard.settings.items!.forEach(item => {
              if (item.key === 'default') return;
              if (!item.key) return;
              itemMap[item.key] = item.label;
            });
            state.stampCards[stampCardId] = itemMap;
          })
          .catch(e => {
            console.error(e);
          });
        return;
      } catch (e) {
        console.error(e);
      }
    };

    const infiniteHandler = async ($state: StateChanger) => {
      try {
        // PointTransfer取得
        const stamps = await getHistory(CHANNEL_ID, state.id, state.from, state.limit);
        if (stamps.length) {
          // スタンプ名取得
          for (const stamp of stamps) {
            await getStampItemLabel(stamp);
          }
          stampHistories.value.push(...stamps);
          state.from += stamps.length;
          $state.loaded();
        } else {
          $state.complete();
        }
      } catch (e) {
        $state.error();
        throw e;
      }
    };

    const back = () => {
      router.back();
    };

    watch(
      () => route,
      async (newId, oldId) => {
        console.log('id changed', newId, oldId);
        state.id = newId.params.id;
        state.from = 0;
        state.limit = 50;
        stampHistories.value = [];
      }
    );

    const maxLabelWidth = computed(() =>
      Math.max(...stampHistories.value.map(history => actionTypeLabel(history).split('').length))
    );

    return {
      state,
      stampHistories,
      currentStampTransfer,
      isShopNameModalActive,
      shopDetail,
      actionTypeLabel,
      tagType,
      infiniteHandler,
      back,
      datetimeFormat,
      showShopNameDetail,
      maxLabelWidth,
    };
  },
});
